import { useGetList, useStore } from "ra-core";
import { useMemo } from "react";

import type { Contact } from "../types";
import type { SentEmail } from "../contacts/EmailComposeSheet";

/**
 * Real mail activity for the Inbox, from the event spine - email.received
 * (BCC/forward capture, already real) and email.sent (compose-and-send,
 * EmailComposeSheet.tsx). Replaces the old approach of reading "sent" mail
 * from a browser-local store: that made the Inbox's own Sent folder
 * per-device and gone on a cleared browser, even though the underlying
 * send was already durably logged elsewhere. The demo preview has no
 * activity_events table, so it keeps the deterministic/session-local
 * behavior instead.
 */

const IS_DEMO = import.meta.env.VITE_IS_DEMO === "true";

export interface MailRow {
  id: string;
  direction: "in" | "out";
  contactId?: string | number;
  contactName: string;
  address: string;
  subject: string;
  body: string;
  /** Only ever set in the demo preview - real rows only ever carry a plain-text preview. */
  bodyHtml?: string;
  date: string;
  unread?: boolean;
}

interface EmailEventRow {
  id: string;
  contact_id: number | null;
  occurred_at: string;
  payload: {
    subject?: string;
    bodyPreview?: string;
  };
}

/** Demo only - fabricated mail attached to real contacts must never appear live. */
const SAMPLE_SUBJECTS: [string, string][] = [
  ["Re: proposal follow-up", "Thanks for sending this over - a couple of questions on the timeline before we take it to the team…"],
  ["Quick question on pricing", "Before our call Thursday, can you clarify how the retainer scales if we add the second product line…"],
  ["Re: intro from the webinar", "Great session last week. We're evaluating partners for Q4 and your case study caught my eye…"],
  ["Scheduling next steps", "Wednesday afternoon works on our side. I'll bring our ops lead so we can dig into the integration…"],
  ["Re: capacity planning deck", "The deck landed well internally. Two stakeholders want a deeper look at the reporting piece…"],
  ["Contract questions", "Legal flagged two clauses - nothing major, but can we walk through them before signature…"],
  ["Re: kickoff agenda", "Agenda looks good. Adding our plant manager to the invite so the data-access questions get answered live…"],
  ["Following up from the trade show", "Good to meet at booth 214. You mentioned a manufacturing-specific playbook - is that something you share…"],
];

export const useInboxActivity = (contacts: Contact[]): MailRow[] => {
  const [demoSent] = useStore<SentEmail[]>("slate.sentEmails", []);

  const { data: sentEvents = [] } = useGetList<EmailEventRow>(
    "activity_events",
    {
      filter: { type: "email.sent" },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "occurred_at", order: "DESC" },
    },
    { enabled: !IS_DEMO, onError: () => {} },
  );
  const { data: receivedEvents = [] } = useGetList<EmailEventRow>(
    "activity_events",
    {
      filter: { type: "email.received" },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "occurred_at", order: "DESC" },
    },
    { enabled: !IS_DEMO, onError: () => {} },
  );

  return useMemo(() => {
    if (IS_DEMO) {
      const inbound: MailRow[] = contacts.slice(0, 8).map((c, i) => ({
        id: `in-${c.id}`,
        direction: "in",
        contactId: c.id,
        contactName: `${c.first_name} ${c.last_name}`,
        address: c.email_jsonb?.[0]?.email ?? "",
        subject: SAMPLE_SUBJECTS[i % SAMPLE_SUBJECTS.length]![0],
        body: SAMPLE_SUBJECTS[i % SAMPLE_SUBJECTS.length]![1],
        date: c.last_seen,
        unread: i < 3,
      }));
      const outbound: MailRow[] = demoSent.map((s) => ({
        id: `out-${s.id}`,
        direction: "out",
        contactId: s.contactId,
        contactName: s.contactName,
        address: s.to,
        subject: s.subject,
        body: s.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " "),
        bodyHtml: s.body,
        date: s.date,
      }));
      return [...outbound, ...inbound].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
    }

    const contactsById = new Map(contacts.map((c) => [c.id, c]));
    const rowFor = (direction: "in" | "out") => (e: EmailEventRow): MailRow | null => {
      if (e.contact_id == null) return null;
      const c = contactsById.get(e.contact_id);
      return {
        id: e.id,
        direction,
        contactId: e.contact_id,
        contactName: c ? `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() : `Contact #${e.contact_id}`,
        address: c?.email_jsonb?.[0]?.email ?? "",
        subject: e.payload.subject ?? "(no subject)",
        body: e.payload.bodyPreview ?? "",
        date: e.occurred_at,
      };
    };

    const rows = [
      ...sentEvents.map(rowFor("out")),
      ...receivedEvents.map(rowFor("in")),
    ].filter((r): r is MailRow => r != null);
    return rows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [contacts, demoSent, sentEvents, receivedEvents]);
};
